import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FilterOptions } from '../../types/reports';
import { useTranslation } from 'react-i18next';

interface ReportsFiltersProps {
  filters: {
    startDate: Date | null;
    endDate: Date | null;
    agentId: string;
    userId: string;
    leadQuality: string;
    conversationStatus: string;
    agentStatus: string;
    status: string;
    typeProduct: string;
    groupBy: string;
  };
  filterOptions: FilterOptions | null;
  onFilterChange: (filters: any) => void;
  onApply: () => void;
  onCancel: () => void;
}

const ReportsFilters: React.FC<ReportsFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onApply,
  onCancel
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChange = (field: string, value: any) => {
    onFilterChange({
      ...filters,
      [field]: value
    });
  };

  const handleClearFilters = () => {
    onFilterChange({
      startDate: null,
      endDate: null,
      agentId: '',
      userId: '',
      leadQuality: '',
      conversationStatus: '',
      agentStatus: '',
      status: '',
      typeProduct: '',
      groupBy: 'day'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Date Range */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <DatePicker
            label={t('pages.reports.filters.startDate')}
            value={filters.startDate}
            onChange={(date) => handleChange('startDate', date)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small'
              }
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <DatePicker
            label={t('pages.reports.filters.endDate')}
            value={filters.endDate}
            onChange={(date) => handleChange('endDate', date)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small'
              }
            }}
          />
        </Box>

        {/* Lead Quality */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pages.reports.filters.leadQuality')}</InputLabel>
            <Select
              value={filters.leadQuality}
              onChange={(e) => handleChange('leadQuality', e.target.value)}
              label={t('pages.reports.filters.leadQuality')}
            >
              <MenuItem value="">{t('pages.reports.filters.allQualities')}</MenuItem>
              {filterOptions?.leadQualities.map((quality) => (
                <MenuItem key={quality} value={quality}>
                  {quality}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Conversation Status */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pages.reports.filters.conversationStatus')}</InputLabel>
            <Select
              value={filters.conversationStatus}
              onChange={(e) => handleChange('conversationStatus', e.target.value)}
              label={t('pages.reports.filters.conversationStatus')}
            >
              <MenuItem value="">{t('pages.reports.filters.all')}</MenuItem>
              {filterOptions?.conversationStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Agent Status */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pages.reports.filters.agentStatus')}</InputLabel>
            <Select
              value={filters.agentStatus}
              onChange={(e) => handleChange('agentStatus', e.target.value)}
              label={t('pages.reports.filters.agentStatus')}
            >
              <MenuItem value="">{t('pages.reports.filters.all')}</MenuItem>
              {filterOptions?.agentStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Type Product */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pages.reports.filters.typeProduct')}</InputLabel>
            <Select
              value={filters.typeProduct}
              onChange={(e) => handleChange('typeProduct', e.target.value)}
              label={t('pages.reports.filters.typeProduct')}
            >
              <MenuItem value="">{t('pages.reports.filters.all')}</MenuItem>
              {filterOptions?.typeProducts.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Group By */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pages.reports.filters.groupBy')}</InputLabel>
            <Select
              value={filters.groupBy}
              onChange={(e) => handleChange('groupBy', e.target.value)}
              label={t('pages.reports.filters.groupBy')}
            >
              {filterOptions?.groupByOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`pages.reports.filters.groupByOptions.${option}`, { defaultValue: option })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Agent ID */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <TextField
            fullWidth
            size="small"
            label={t('pages.reports.filters.agentId')}
            value={filters.agentId}
            onChange={(e) => handleChange('agentId', e.target.value)}
            placeholder={t('pages.reports.filters.agentIdPlaceholder')}
          />
        </Box>

        {/* User ID */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <TextField
            fullWidth
            size="small"
            label={t('pages.reports.filters.userId')}
            value={filters.userId}
            onChange={(e) => handleChange('userId', e.target.value)}
            placeholder={t('pages.reports.filters.userIdPlaceholder')}
          />
        </Box>

        {/* Status */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <TextField
            fullWidth
            size="small"
            label={t('pages.reports.filters.status')}
            value={filters.status}
            onChange={(e) => handleChange('status', e.target.value)}
            placeholder={t('pages.reports.filters.statusPlaceholder')}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          size="small"
        >
          {t('pages.reports.filters.clear')}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          size="small"
        >
          {t('pages.reports.filters.cancel')}
        </Button>
        <Button
          variant="contained"
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          onClick={onApply}
          size="small"
        >
          {t('pages.reports.filters.apply')}
        </Button>
      </Box>
    </Box>
  );
};

export default ReportsFilters;
